import { useRef, useState } from 'react'
import {useConstant, useLazyConstant, useRefState} from '@startlibs/core'
import _ from 'lodash/fp'
import axios from 'axios'
import { isMultiFile, RecordFormat } from '../utils'
import { callIfFunction, formatDate } from '@startlibs/utils'
import { dateMessages } from '../utils/dateMessages'
import {EventLogStatus, RecordStatus} from '../utils/recordEnums'
import {appendLog} from './uploadLog'

const insertRecord = (newRecord,getFileProgress) => records => {
  const cleanedRecord = _.flow(
    _.unset('files'),
    _.unset('date'),
    _.set('onlyDelete', true),
    _.set('fileProgress',getFileProgress(newRecord.key))
  )(newRecord)
  if (newRecord.recordFormat !== RecordFormat.DicomStudy || !newRecord.studyUID) {
    return records.concat(cleanedRecord)
  }
  const index = _.findIndex(['studyUID', newRecord.studyUID], records)
  if (index < 0) {
    return records.concat(cleanedRecord)
  }

  // update original key to sync with recordFiles
  return _.update([index], (record) =>
    _.flow(
      _.set('fileProgress', getFileProgress(record.key)),
      _.set('onlyDelete', true),
      _.unset('date'),
      _.update('studyModalities',_.union(newRecord.studyModalities))
    )(record),
    records
  )
}

const updateFormattedDate = record => {
  const date = record.sortDate ? new Date(record.sortDate) : new Date()
  return _.set('date', formatDate(date, 'L dd, yyyy', dateMessages, true), record)
}

const insertManyRecords = (newRecords,getFileProgress) => records =>
  newRecords.reduce((updatedRecords, newRecord) => insertRecord(newRecord,getFileProgress)(updatedRecords), records)

const enqueueBySize = (record, recordsFilesList) => {
  const size =
    _.sumBy(_.get(['file', 'size']), record.files) +
    _.sumBy(_.get(['file', 'size']), record.uploading) +
    _.sumBy(_.get(['file', 'size']), record.uploaded)

  const index = _.findIndex(
    recordFiles => recordFiles.size > size && !recordFiles.uploading,
    recordsFilesList
  )
  const recordFiles = { ...record, size }
  if (index >= 0) {
    return [...recordsFilesList.slice(0, index), recordFiles, ...recordsFilesList.slice(index)]
  }

  const lastNotUploadingIndex = _.findLastIndex(
    recordFilesItem => !recordFilesItem.uploading,
    recordsFilesList
  )
  if (lastNotUploadingIndex >= 0) {
    return [
      ...recordsFilesList.slice(0, lastNotUploadingIndex),
      recordFiles,
      ...recordsFilesList.slice(lastNotUploadingIndex),
    ]
  }
  return recordsFilesList.concat(recordFiles)
}

const reenqueue = (record, recordsFiles, index) => {
  const previous = recordsFiles[index]
  const updated = _.update('files', _.concat(record.files), previous)
  return enqueueBySize(updated, [...recordsFiles.slice(0, index), ...recordsFiles.slice(index + 1)])
}

const enqueueRecordFiles = record => recordsFiles => {
  if (record.recordFormat !== RecordFormat.DicomStudy || !record.studyUID) {
    return enqueueBySize(record, recordsFiles)
  }
  const index = recordsFiles.findIndex(_.matchesProperty('studyUID', record.studyUID))
  if (index < 0) {
    return enqueueBySize(record, recordsFiles)
  }
  return reenqueue(record, recordsFiles, index)
}

const enqueueManyRecordFiles = records => files =>
  records.reduce((updatedFiles, record) => enqueueRecordFiles(record)(updatedFiles), files)

const useRecordQueue = () => {
  const filesRef = useRef([])

  const setRecord = useConstant(
    _.curry((recordKey, updater) => {
      const index = _.findIndex(['key', recordKey], filesRef.current)
      if (index < 0) {
        return null
      }
      if (!updater) {
        filesRef.current = [
          ...filesRef.current.slice(0, index),
          ...filesRef.current.slice(index + 1),
        ]
        return null
      }

      filesRef.current = _.update(
        [index],
        value => callIfFunction(updater, value),
        filesRef.current
      )
      return filesRef.current[index]
    })
  )

  const getRecord = useConstant(recordKey => _.find(['key', recordKey], filesRef.current))

  const enqueueRecords = useConstant(records => {
    filesRef.current = enqueueManyRecordFiles(records)(filesRef.current)
  })

  const getNextInQueue = useConstant(smallest =>
    smallest
      ? _.find(_.get('files.length'), filesRef.current)
      : _.findLast(_.get('files.length'), filesRef.current)
  )

  return [getRecord, setRecord, getNextInQueue, enqueueRecords]
}

const getMultiFileUID = async record => {
  if (!isMultiFile(record) || record.params.multiFileUID) {
    return null
  }
  const response = await axios.post('/uploader/init-multifile')
  return response.data.multiFileUID
}

const getFileProgress = (queuedRecord) =>
  ({
    uploaded: (queuedRecord.uploaded?.length || 0),
    paused: (queuedRecord.paused?.length || 0),
    total: queuedRecord.files.length + (queuedRecord.failed?.length || 0) + (queuedRecord.uploading?.length || 0) + (queuedRecord.uploaded?.length || 0) + (queuedRecord.paused?.length || 0),
    failed: queuedRecord.failed?.length || 0,
    uploading: queuedRecord.uploading?.length || 0,
    queued: queuedRecord.files.length
  })


export const useRecordStateManager = initialRecords => {
  const [records, setRecords] = useState(initialRecords)

  const [getQueuedRecord, setQueuedRecord, getNextInQueue, enqueueRecords] = useRecordQueue()

  const recentlyQuarantined = useRef({})

  const updateRecordByKey = useConstant(
    _.curry((key, update) =>
      setRecords(
        _.map(i => {
          if (i.key !== key) {
            return i
          }
          return callIfFunction(update, i)
        })
      )
    )
  )

  const completeRecord = useConstant((recordKey, recordUID, quarantined,response) => {
    const queuedRecord = getQueuedRecord(recordKey)
    if (!queuedRecord) {
      return recordUID
    }
    //setQueuedRecord(recordKey, null)
    updateRecordByKey(recordKey, record =>
      _.flow(
        _.set('recordUID', recordUID),
        _.update(['studyUID'],(s) =>  response?.dicomAttributes?.StudyUID || s),
        updateFormattedDate,
        _.update('sortDate', s => s || new Date()),
        _.set('status', record.quarantined || quarantined ? RecordStatus.Failed : RecordStatus.Uploaded),
        _.set('actions', quarantined ? 'delete' : 'complete'),
        quarantined ? _.set('showDialog', true) : _.identity,
        quarantined ? _.set('dialogVariant', 'warning') : _.identity,
        _.set('progress', 100),
        _.set('downloadEnabled', true),
        _.update('quarantined', q => q || quarantined)
      )(record)
    )
    return recordUID
  })

  const uploadSuccess = useConstant(async (recordKey, fileUploaded, response) => {
    appendLog([fileUploaded.file], EventLogStatus.Uploaded)
    let recordUID = response.medicalRecordID
    const queuedRecord = getQueuedRecord(recordKey)
    if (!queuedRecord) {
      return recordUID
    }

    const updatedQueuedRecord = setQueuedRecord(
      recordKey,
      _.flow(
        _.update(['uploading'], _.without([fileUploaded])),
        _.update(['uploaded'], (uploaded = []) => uploaded.concat(fileUploaded)),
        recordUID ? _.identity : _.set(['recordUID'], recordUID)
      )
    )
    incrementProgress(recordKey, 0)
    if (
      updatedQueuedRecord.files.length ||
      updatedQueuedRecord.uploading.length ||
      updatedQueuedRecord.failed
    ) {
      if (response.quarantined) {
        recentlyQuarantined.current = _.set(
          recordKey,
          _.update('quarantined', (q = []) => q.concat(fileUploaded), updatedQueuedRecord),
          recentlyQuarantined.current
        )
        updateRecordByKey(
          recordKey,
          _.assign(_,
            {
              quarantined: response.quarantined,
              fileProgress: getFileProgress(queuedRecord)
            }
          )
        )
      }
      if (recordUID && !updatedQueuedRecord.recordUID) {
        updateRecordByKey(recordKey, _.assign(_,
          {
            recordUID,
            fileProgress: getFileProgress(queuedRecord)
          }
        ))
      }
      return recordUID
    }
    if (isMultiFile(updatedQueuedRecord) && updatedQueuedRecord.params.multiFileUID) {
      if (recentlyQuarantined.current[recordKey] || response.quarantined) {
        return completeRecord(recordKey, recordUID, true, response)
      }
      const { data } = await axios.post('/uploader/end-multifile', queuedRecord.params)
      recordUID = data.medicalRecordID
      if (queuedRecord.metadata) {
        await axios.put(`/record/${recordUID}/metadata`, queuedRecord.metadata)
      }
    }
    if (queuedRecord.metadata) {
      await axios.put(`/record/${recordUID}/metadata`, queuedRecord.metadata)
    }
    return completeRecord(recordKey, recordUID, response.quarantined, response)
  })

  const incrementProgress = useConstant(
    _.curry((recordKey, delta) => {
      const queuedRecord = getQueuedRecord(recordKey)
      if (!queuedRecord) {
        return
      }
      const updatedRecord = setQueuedRecord(
        recordKey,
        _.update(['uploadedBytes'], (uploadedBytes = 0) => uploadedBytes + delta)
      )
      const progress = (updatedRecord.uploadedBytes * 100) / updatedRecord.size
      updateRecordByKey(recordKey, _.assign(
        _,
        {
          status: queuedRecord.status === RecordStatus.Paused ? RecordStatus.Paused : RecordStatus.Uploading,
          fileProgress: getFileProgress(queuedRecord),
          progress: Math.round(progress)
        }
      ))
    })
  )

  const uploadFailure = useConstant(async (recordKey, fileUploaded, failure, progressDelta, errorMessage = '') => {
    const queuedRecord = getQueuedRecord(recordKey)
    const isPaused = queuedRecord?.status === RecordStatus.Paused
    appendLog([fileUploaded.file], isPaused  ? EventLogStatus.Paused : EventLogStatus.Failed, errorMessage)
    if (!queuedRecord) {
      return
    }
    setQueuedRecord(
      recordKey,
      _.flow(
        _.update(['uploading'], _.without([fileUploaded])),
        _.update(isPaused ? ['paused'] : ['failed'], (uploaded = []) => uploaded.concat({ ...fileUploaded, failure }))
      )
    )
    incrementProgress(recordKey, progressDelta)
    if (!isPaused) {
      updateRecordByKey(
        recordKey,
        _.flow(
          updateFormattedDate,
          _.set('status', RecordStatus.Failed),
          _.set('failed', failure)
        )
      )
    }
  })

  const queueToRetry = useConstant(async record => {
    const queuedRecord = getQueuedRecord(record.key)
    if (!queuedRecord) {
      return
    }
    const {failed = [],paused = []} = queuedRecord
    appendLog([].concat(failed,paused).map(_.get('file')), EventLogStatus.Waiting)
    setQueuedRecord(
      record.key,
      _.flow(_.update(['files'], _.concat([...failed,...paused])), _.unset(['failed']), _.unset(['status']), _.unset(['paused']))
    )
    incrementProgress(record.key, 0)
    updateRecordByKey(
      record.key,
      _.flow(
        _.set('date', null),
        _.unset('failed')
      )
    )
  })

  const pauseRecord = useConstant(async recordKey => {
    const queuedRecord = getQueuedRecord(recordKey)
    if (!queuedRecord) {
      return
    }
    const {failed = [],files = []} = queuedRecord
    appendLog([].concat(failed,files).map(_.get('file')), EventLogStatus.Paused)
    setQueuedRecord(
      recordKey,
      _.flow(
        _.update(['paused'],(paused = []) => paused.concat(failed,files)),
        _.set(['files'],[]),
        _.unset(['failed']),
        _.set('status',RecordStatus.Paused)
      )
    )
    updateRecordByKey(
      recordKey,
      _.flow(
        _.set('status',RecordStatus.Paused),
        _.set('date', null)
      )
    )
  })

  const consumeFile = useConstant(async smallest => {
    const queuedRecord = getNextInQueue(smallest)
    if (!queuedRecord) {
      return []
    }
    const recordKey = queuedRecord.key
    const multiFileUID = await getMultiFileUID(queuedRecord)

    const consumeFileInRecord = () => {
      const queuedRecordAfterAwait = getQueuedRecord(recordKey)
      const fileToUpload = _.get(['files', 0], queuedRecordAfterAwait)
      if (!fileToUpload) {
        return consumeFile()
      }
      const updatedRecord = setQueuedRecord(
        recordKey,
        _.flow(
          multiFileUID ? _.set(['params', 'multiFileUID'], multiFileUID) : _.identity,
          _.update(['files'], files => files.slice(1)),
          _.update(['uploading'], (uploading = []) => uploading.concat(fileToUpload))
        )
      )
      appendLog([fileToUpload.file], EventLogStatus.Uploading)
      return [fileToUpload, recordKey, updatedRecord.params]
    }

    return consumeFileInRecord(recordKey, multiFileUID)
  })

  const manager = useLazyConstant(() => ({
    insertMany: newRecords => {
      enqueueRecords(newRecords)
      setRecords(insertManyRecords(newRecords,(recordKey) => getFileProgress(getQueuedRecord(recordKey))))
    },
    consumeFile,
    updateRecord: (obj, update) => updateRecordByKey(obj.key, update),
    removeRecord: record => {
      setQueuedRecord(record.key, null)
      setRecords(_.differenceBy('key', _, [record]))
    },
    uploadSuccess,
    uploadFailure,
    incrementProgress,
    queueToRetry,
    pauseRecord,
    getQueuedRecord,
    getRecentlyQuarantinedRecord: record => recentlyQuarantined.current[record.key],
  }))

  return [records, manager]
}
